import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Flame, Beef, Wheat, Droplet, UtensilsCrossed, Scale, Dumbbell, Pencil, Check, X, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const OBJECTIVE_LABELS = {
  cut: 'Seche (−500)',
  perte_gras: 'Perte de gras (−400)',
  perte: 'Perte de poids (−300)',
  bulk: 'Prise de masse (+300)',
  recomp: 'Maintien (0)',
};

function CalcExplainModal({ profile, trainingBurned, targetCalories, onClose }) {
  const p = profile || {};
  const steps = [
    { num: '1', label: 'Métabolisme de base (BMR)', detail: `${p.weight}kg · ${p.height}cm · ${p.age} ans · ${p.gender === 'female' ? 'Femme' : 'Homme'}`, value: `${p.bmr ?? '—'} kcal` },
    { num: '2', label: 'Repos (BMR × 1,2)', detail: 'Vie quotidienne sans sport', value: `${p.repos ?? '—'} kcal` },
    { num: '3', label: 'Sport du jour', detail: 'Séance + activités ajoutées', value: `+${trainingBurned || 0} kcal` },
    { num: '4', label: 'Ajustement objectif', detail: OBJECTIVE_LABELS[p.objective] || 'Maintien', value: `${(p.kcal_adjust ?? 0) > 0 ? '+' : ''}${p.kcal_adjust ?? 0} kcal` },
  ];
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-end justify-center p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="bg-[#1B1B1B] rounded-t-2xl sm:rounded-xl w-full max-w-md pb-safe mb-[60px] sm:mb-0" onClick={e => e.stopPropagation()} data-testid="calc-explain-modal">
        <div className="pt-2 pb-1 flex justify-center"><div className="w-10 h-1 rounded-full bg-zinc-600" /></div>
        <div className="px-5 pb-6 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-lg text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              <Flame className="w-4 h-4 text-[#FF6B35] inline mr-1 mb-0.5" /> Comment est calculé ton objectif ?
            </p>
            <button onClick={onClose} className="text-zinc-500 hover:text-white text-xl" data-testid="close-calc-modal">&times;</button>
          </div>
          <div className="space-y-2">
            {steps.map(s => (
              <div key={s.num} className="flex items-center gap-3 bg-[#0F0F0F] rounded-lg px-3 py-2.5">
                <span className="w-6 h-6 rounded-full bg-[#FF6B35]/15 text-[#FF6B35] text-[11px] font-bold flex items-center justify-center shrink-0">{s.num}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white font-medium">{s.label}</p>
                  <p className="text-[10px] text-zinc-500">{s.detail}</p>
                </div>
                <span className="font-mono text-sm font-bold text-[#FFB800]">{s.value}</span>
              </div>
            ))}
          </div>
          <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-lg px-3 py-2.5 flex items-center justify-between">
            <p className="text-xs text-[#FF6B35] font-bold">= Ton objectif du jour</p>
            <span className="font-mono text-lg font-black text-[#FF6B35]">{targetCalories} kcal</span>
          </div>
          <p className="text-[10px] text-zinc-500 leading-relaxed">
            Ta dernière pesée, ta séance de sport et ton objectif (modifiable dans Profil) mettent à jour ce calcul automatiquement, partout dans l'app.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function MacroNeedsCard({ mealsData, macroTargets, macroRemaining, trainingBurned, profileNutrition, onTrainingUpdate }) {
  const [editingBurned, setEditingBurned] = useState(false);
  const [burnedValue, setBurnedValue] = useState('');
  const [showCalc, setShowCalc] = useState(false);

  const handleSaveBurned = async () => {
    const val = parseInt(burnedValue);
    if (isNaN(val) || val < 0) return;
    try {
      await apiClient.put('/training/today/adjust', { total_calories: val });
      if (onTrainingUpdate) onTrainingUpdate(val);
      setEditingBurned(false);
      toast.success('Calories ajustees');
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4" data-testid="macro-needs">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          <Flame className="w-5 h-5 text-[#FF6B35] inline mr-1 mb-0.5" /> Besoins du jour
        </h2>
        <button onClick={() => setShowCalc(true)} className="text-zinc-500 hover:text-[#FF6B35] p-1 transition-colors" data-testid="calc-help-btn" aria-label="Comment est calculé mon objectif ?">
          <HelpCircle className="w-4 h-4" />
        </button>
      </div>
      {showCalc && <CalcExplainModal profile={profileNutrition} trainingBurned={trainingBurned} targetCalories={macroTargets.calories} onClose={() => setShowCalc(false)} />}

      <div className="flex items-end justify-between mb-2">
        <div>
          <span className="font-mono text-4xl font-bold text-white" data-testid="calories-consumed">{Math.round(mealsData.calories)}</span>
          <span className="text-base text-zinc-500 ml-1">/ {macroTargets.calories}</span>
          {trainingBurned > 0 && (
            <p className="text-[9px] text-[#FF6B35]/80 leading-none mt-1" data-testid="sport-included-label">
              objectif sport compris (+{trainingBurned} kcal)
            </p>
          )}
        </div>
        <div className="text-right">
          <span className={`font-mono text-base font-bold ${macroRemaining.calories >= 0 ? 'text-[#FF6B35]' : 'text-red-400'}`}>
            {macroRemaining.calories >= 0 ? macroRemaining.calories : `+${Math.abs(macroRemaining.calories)}`}
            <span className="text-xs text-zinc-500 ml-1">kcal</span>
          </span>
          <p className={`text-[9px] leading-none ${macroRemaining.calories >= 0 ? 'text-zinc-500' : 'text-red-400/80'}`} data-testid="remaining-label">
            {macroRemaining.calories >= 0 ? 'restantes' : 'dépassement'}
          </p>
        </div>
      </div>
      <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden mb-4">
        <div className="h-full bg-gradient-meute rounded-full transition-all duration-700"
          style={{ width: `${Math.min(100, (mealsData.calories / macroTargets.calories) * 100)}%` }} />
      </div>

      {/* Calories burned from training — always visible */}
      <div className="flex items-center justify-between mb-3 px-2 py-1.5 bg-[#FF6B35]/10 rounded-lg" data-testid="training-burned-row">
        <Link to="/entrainement?tab=calories" className="text-xs text-[#FF6B35] flex items-center gap-1 hover:underline">
          <Dumbbell className="w-3 h-3" /> Brule a l'entrainement
        </Link>
        {!editingBurned ? (
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-sm font-bold text-[#FF6B35]" data-testid="burned-value">
              {trainingBurned > 0 ? `-${trainingBurned}` : '0'} kcal
            </span>
            <button onClick={() => { setEditingBurned(true); setBurnedValue(String(trainingBurned || 0)); }}
              className="text-zinc-500 hover:text-[#FF6B35] p-0.5" data-testid="edit-burned-btn">
              <Pencil className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <input type="number" value={burnedValue} onChange={e => setBurnedValue(e.target.value)}
              className="w-16 bg-[#0F0F0F] border border-[#FF6B35]/50 rounded text-xs text-white font-mono px-1.5 py-0.5 text-right"
              autoFocus onKeyDown={e => { if (e.key === 'Enter') handleSaveBurned(); if (e.key === 'Escape') setEditingBurned(false); }}
              data-testid="burned-input" />
            <button onClick={handleSaveBurned} className="text-green-400 p-0.5"><Check className="w-3.5 h-3.5" /></button>
            <button onClick={() => setEditingBurned(false)} className="text-zinc-500 p-0.5"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      <div className="space-y-2 mb-4">
        {[
          { key: 'protein', label: 'Protéines', icon: Beef, color: '#FF00B8', eaten: Math.round(mealsData.protein), target: macroTargets.protein, remaining: macroRemaining.protein },
          { key: 'carbs', label: 'Glucides', icon: Wheat, color: '#FF6B35', eaten: Math.round(mealsData.carbs), target: macroTargets.carbs, remaining: macroRemaining.carbs },
          { key: 'fat', label: 'Lipides', icon: Droplet, color: '#FFB800', eaten: Math.round(mealsData.fat), target: macroTargets.fat, remaining: macroRemaining.fat },
        ].map(({ key, label, icon: Icon, color, eaten, target }) => (
          <div key={key} data-testid={`${key}-progress`}>
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-1.5">
                <Icon className="w-3 h-3" style={{ color }} />
                <span className="text-xs text-zinc-400">{label}</span>
              </div>
              <span className="font-mono text-xs text-zinc-300">{eaten}<span className="text-zinc-500">/{target}g</span></span>
            </div>
            <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, target > 0 ? (eaten / target) * 100 : 0)}%`, backgroundColor: color }} />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="quick-actions">
        <Link to="/nutrition" className="flex flex-col items-center gap-1.5 p-3 bg-[#0F0F0F] rounded-lg border border-[#2A2A2E] hover:border-[#FF6B35]/40 transition-colors" data-testid="quick-add-meal">
          <UtensilsCrossed className="w-5 h-5 text-[#FF6B35]" />
          <span className="text-[10px] text-zinc-400 font-semibold">+ Repas</span>
        </Link>
        <Link to="/profil" className="flex flex-col items-center gap-1.5 p-3 bg-[#0F0F0F] rounded-lg border border-[#2A2A2E] hover:border-[#FF00B8]/40 transition-colors" data-testid="quick-add-weight">
          <Scale className="w-5 h-5 text-[#FF00B8]" />
          <span className="text-[10px] text-zinc-400 font-semibold">+ Pesee</span>
        </Link>
        <Link to="/entrainement?tab=calories" className="flex flex-col items-center gap-1.5 p-3 bg-[#0F0F0F] rounded-lg border border-[#2A2A2E] hover:border-[#FFB800]/40 transition-colors" data-testid="quick-add-exercise">
          <Dumbbell className="w-5 h-5 text-[#FFB800]" />
          <span className="text-[10px] text-zinc-400 font-semibold">+ Exercice</span>
        </Link>
      </div>
    </div>
  );
}
